import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useWallet } from '@aptos-labs/wallet-adapter-react';
import { useQueryClient } from '@tanstack/react-query';
import {
	convertFromBaseUnits,
	getAptosBalance,
	getUSDCBalance,
} from 'core/vaultTransactions';
import { useUserContext } from 'context/userContext';
import {
	queryKeys,
	usePosition,
	useUser,
	useUserLastPositionId,
} from 'api/hooks';
import { getUserApyValue, PortfolioResponse } from 'api/client';
import { executeDepositFlow } from 'core/depositFlow';
import { AppState } from './styles/constants';
import { LoginView } from './components/LoginView';
import { LoadingView } from './components/LoadingView';
import { DepositingView } from './components/DepositingView';
import { GeneratingView } from './components/GeneratingView';
import { ActiveDashboardView } from './components/ActiveDashboardView';
import { notifyDeposit, notifySlackError } from 'utils/slack/slackNotifier';
import { MAX_AMOUNT_ALLOWED, MIN_AMOUNT_ALLOWED } from 'data/constant';
import { EmptyDashboardView } from 'pages/AtlasDashboard/components/EmptyDashboardView';
import { fetchIpInformation } from 'utils/geolocation';
import { NotServedModal } from 'pages/NotServedModal';
import { AGENTS_LIST } from 'data/Agents';

export const AtlasDashboard = () => {
	const shouldEnforceLocationCheck = useMemo(() => {
		if (typeof window === 'undefined') return false;

		const { hostname } = window.location;
		return (
			hostname === 'atlas.rndm.io' ||
			hostname.endsWith('.vercel.app')
		);
	}, []);

	const isHyperionOnly = useMemo(() => {
		if (typeof window === 'undefined') return false;

		const params = new URLSearchParams(window.location.search);
		const vaultParam =
			params.get('vault') || params.get('valut') || params.get('Vault');

		return (vaultParam || '').toLowerCase() === 'hyperion';
	}, []);

	const filteredAgentsList = useMemo(() => {
		if (!isHyperionOnly) return AGENTS_LIST;

		const hyperionAgents = AGENTS_LIST.filter(
			(agent) => agent.strategy?.toLowerCase() === 'hyperion',
		);

		return hyperionAgents.length > 0 ? hyperionAgents : AGENTS_LIST;
	}, [isHyperionOnly]);

	const [currentState, setCurrentState] = useState<AppState>('login');
	const [isConnected, setIsConnected] = useState(false);
	const [balance, setBalance] = useState(0);
	const [totalYield, setTotalYield] = useState(0);
	const [depositAmount, setDepositAmount] = useState('');
	const [yieldProgress, setYieldProgress] = useState(0);
	const [usdcBalance, setUsdcBalance] = useState(0);
	const [fetchingBalance, setFetchingBalance] = useState(false);
	const [aptosBalance, setAptosBalance] = useState(0);
	const [selectedAgentId, setSelectedAgentId] = useState<number | null>(
		AGENTS_LIST[0]?.id ?? null,
	);
	const [showNotServedModal, setShowNotServedModal] = useState(false);
	const [userApyValue, setUserApyValue] = useState<PortfolioResponse | null>(
		null,
	);
	const queryClient = useQueryClient();
	const { account: activeAccount, signAndSubmitTransaction } = useWallet();

	const selectedAgent = useMemo(
		() =>
			filteredAgentsList.find((agent) => agent.id === selectedAgentId) ||
			filteredAgentsList[0],
		[selectedAgentId, filteredAgentsList],
	);
	const minAmountAllowed = selectedAgent?.minDeposit ?? MIN_AMOUNT_ALLOWED;
	const maxAmountAllowed = selectedAgent?.maxDeposit ?? MAX_AMOUNT_ALLOWED;
	const apiBaseUrl = selectedAgent?.baseURL || '';
	const vaultConfig = useMemo(
		() =>
			selectedAgent?.contractAddress
				? { CONTRACT_ADDRESS: selectedAgent.contractAddress }
				: undefined,
		[selectedAgent],
	);

	const {
		userId: contextUserId,
		walletAddress,
		isLoading: isUserIdLoading,
		isLoggedIn: isSiwaLoggedIn,
		logout: logoutUser,
		setBaseUrl,
		baseUrl,
	} = useUserContext();

	const logoutUserRef = useRef(logoutUser);
	logoutUserRef.current = logoutUser;

	// console.log('userId', contextUserId);

	useEffect(() => {
		if (baseUrl === apiBaseUrl) return;
		setBaseUrl(apiBaseUrl);
	}, [apiBaseUrl, baseUrl, selectedAgentId, setBaseUrl]);

	const { data: lastPositionId, isLoading: lastPositionIdIsLoading } =
		useUserLastPositionId(contextUserId, !!contextUserId, apiBaseUrl);

	const { data: positionData, isLoading: isLoadingPosition } = usePosition(
		lastPositionId ?? null,
		!!lastPositionId,
		apiBaseUrl,
	);

	const { data: userInfo, refetch: refetchUser } = useUser(
		contextUserId,
		!!contextUserId,
		apiBaseUrl,
	);

	useEffect(() => {
		if (!filteredAgentsList.length) return;

		const hasSelectedAgent = filteredAgentsList.some(
			(agent) => agent.id === selectedAgentId,
		);

		if (!hasSelectedAgent) {
			setSelectedAgentId(filteredAgentsList[0]?.id ?? null);
		}
	}, [filteredAgentsList, selectedAgentId]);

	useEffect(() => {
		if (!contextUserId) return;
		refetchUser();
	}, [contextUserId, lastPositionId, refetchUser, apiBaseUrl]);

	useEffect(() => {
		if (!contextUserId || !positionData) {
			setUserApyValue(null);
			return;
		}

		let cancelled = false;

		(async () => {
			try {
				const response = await getUserApyValue(
					contextUserId,
					true,
					apiBaseUrl,
				);
				if (!cancelled) {
					setUserApyValue(response);
				}
			} catch (error) {
				if (!cancelled) {
					// console.error('Failed to fetch user APY value:', error);
					setUserApyValue(null);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [contextUserId, positionData, apiBaseUrl]);

	const totalYieldCal = (() => {
		return Number(
			userApyValue?.totals?.apySinceAllocation?.toFixed(2) ?? 0,
		);
	})();

	const hasActivePosition = useMemo(() => {
		if (!positionData?.result || !Array.isArray(positionData.result)) {
			return false;
		}

		const [owner, vaultBalanceRaw, , isFinalized] = positionData.result;
		const vaultBalance =
			Number.isFinite(Number(vaultBalanceRaw || 0)) && vaultBalanceRaw
				? Number(vaultBalanceRaw)
				: 0;

		const lastDeposit = userInfo?.lastDeposit;
		const lastDepositAmount = Number(lastDeposit?.amountBase || 0);
		const ownerMatches = walletAddress === owner;
		const posIdMatches = lastDeposit?.posId === positionData.id;

		return ownerMatches && posIdMatches && lastDepositAmount > 0;
	}, [positionData, userInfo, walletAddress]);

	useEffect(() => {
		if (isSiwaLoggedIn && walletAddress && currentState === 'login') {
			setIsConnected(true);

			setCurrentState('loading');
		}
	}, [isSiwaLoggedIn, walletAddress, currentState]);

	const handleDeposit = async () => {
		const depositValue = Number(depositAmount);

		if (!Number.isFinite(depositValue) || depositValue <= 0) {
			toast.error('Please enter a valid amount');
			return;
		}

		if (!activeAccount || !contextUserId) {
			/*console.error('❌ Deposit authentication check failed:', {
				hasActiveAccount: !!activeAccount,
				hasUserId: !!contextUserId,
				isUserIdLoading,
			})*/

			if (isUserIdLoading) {
				toast.error('Loading your profile, please wait...', {
					autoClose: 3000,
				});
				return;
			}

			if (activeAccount && !contextUserId) {
				// User has wallet connected but no userId - session issue
				toast.error(
					'Session expired. Please log out and sign in again.',
					{ autoClose: 7000 },
				);
			} else {
				toast.error('Please sign in with Aptos Connect first');
			}
			return;
		}

		if (contextUserId.startsWith('0x')) {
			/* console.error(
				'❌ Invalid userId detected - wallet address used instead of Firestore ID:',
				{
					contextUserId,
					walletAddress,
				},
			);*/
			toast.error(
				'Authentication error. Please log out and sign in again.',
				{ autoClose: 7000 },
			);

			notifySlackError({
				title: '❌ Invalid userId detected - wallet address used instead of Firestore ID:',
				details: JSON.stringify({
					contextUserId,
					walletAddress,
				}),
				address: walletAddress || '',
			});
			return;
		}

		if (hasActivePosition) {
			toast.warning(
				'You already have an active position. Please withdraw your existing position first.',
			);
			/* console.warn('⚠️ Deposit blocked: Active position exists', {
				positionData,
			});*/
			return;
		}

		if (depositValue > maxAmountAllowed) {
			toast.error(`Maximum deposit is ${maxAmountAllowed} USDC`);
			return;
		}

		const walletBalance = convertFromBaseUnits(usdcBalance);
		if (depositValue > walletBalance) {
			toast.error(
				`Insufficient balance. You have ${walletBalance.toFixed(
					2,
				)} USDC`,
			);
			return;
		}

		if (depositValue < minAmountAllowed) {
			toast.error(`Minimum deposit is ${minAmountAllowed} USDC`);
			return;
		}

		// CRITICAL: Set loading state immediately (synchronous)
		setCurrentState('depositing');

		try {
			// CRITICAL: For Safari compatibility, execute transaction FIRST
			// Then do geolocation check AFTER (non-blocking for user experience)
			const { posIdPromise, recordDepositPromise, positionPromise } =
				await executeDepositFlow(
					activeAccount,
					contextUserId,
					depositValue,
					signAndSubmitTransaction,
					apiBaseUrl,
					vaultConfig,
				);

			// Check geolocation AFTER transaction is submitted
			// This prevents popup blocking in Safari
			if (shouldEnforceLocationCheck) {
				fetchIpInformation()
					.then((isBlockedLocation) => {
						if (isBlockedLocation) {
							/* console.warn(
								'⚠️ Deposit from blocked location detected',
							);*/
							// Log but don't block - transaction already submitted
						}
					})
					.catch((error) => {
						/* console.error(
							'Failed to verify user location after deposit:',
							error,
						);*/
					});
			}

			// Don't block UI: handle post-deposit work in background
			void recordDepositPromise.catch((error) => {
				// console.error('recordDeposit failed after tx:', error);
				toast.error(
					error?.message ||
						'Deposit submitted on-chain, but backend sync failed.',
				);
			});

			posIdPromise
				.then((posId) => {
					if (!Number.isFinite(posId) || posId <= 0) {
						throw new Error(`Invalid posId: ${posId}`);
					}

					void queryClient.invalidateQueries({
						queryKey: queryKeys.userLastPositionId(
							contextUserId,
							apiBaseUrl,
						),
					});
					void queryClient.invalidateQueries({
						queryKey: queryKeys.position(posId, apiBaseUrl),
					});

					// Kick off a second invalidation once indexer catches up
					positionPromise
						.then((position) => {
							if (!position) return;
							queryClient.invalidateQueries({
								queryKey: queryKeys.userLastPositionId(
									contextUserId,
									apiBaseUrl,
								),
							});
							queryClient.invalidateQueries({
								queryKey: queryKeys.position(posId, apiBaseUrl),
							});
						})
						.catch((error) => {
							/* console.error(
								'Background indexer wait failed after deposit:',
								error,
							);*/
						});

					notifyDeposit({
						title: `Deposited Successfully - PositionId:#${posId}`,
						details: `Deposited ${depositValue} USDC `,
						address: walletAddress?.toString(),
					});
				})
				.catch((error) => {
					/* console.error(
						'Post-deposit processing failed (posId/indexer):',
						error,
					);*/
					toast.error(
						'Deposit submitted. We are still syncing your position.',
					);
				});

			setCurrentState('generating');
			setDepositAmount('');

			// After animation, show active dashboard
			setTimeout(async () => {
				setCurrentState('active-dashboard');
			}, 3000);
		} catch (error: any) {
			// console.error('Deposit failed:', error);
			notifyDeposit(
				{
					title: `Deposit ${depositValue} Failed`,
					details:
						error.message || 'Deposit failed. Please try again.',
					address: walletAddress?.toString(),
				},
				'error',
			);
			//toast.error(error.message || 'Deposit failed. Please try again.');
			setCurrentState(
				balance > 0 ? 'active-dashboard' : 'empty-dashboard',
			);
		} finally {
			// Cleanup handled by state transitions
		}
	};

	const handleLogout = async () => {
		// Reset all state
		setCurrentState('login');
		setIsConnected(false);
		setBalance(0);
		setTotalYield(0);
		logoutUser();
	};

	// Handle SIWA logout - reset to login state when user logs out
	useEffect(() => {
		if (!isSiwaLoggedIn && !walletAddress && isConnected) {
			handleLogout();
		}
	}, [isSiwaLoggedIn, walletAddress, isConnected]);

	// Block access for restricted regions at login
	useEffect(() => {
		if (
			!shouldEnforceLocationCheck ||
			!isSiwaLoggedIn ||
			!walletAddress ||
			showNotServedModal
		) {
			return;
		}

		let cancelled = false;

		(async () => {
			try {
				const isBlockedLocation = await fetchIpInformation();

				if (!cancelled && isBlockedLocation) {
					setShowNotServedModal(true);
					setCurrentState('login');
					setIsConnected(false);
					setBalance(0);
					setTotalYield(0);
					logoutUserRef.current();
				}
			} catch (error) {
				if (!cancelled) {
					/* console.error(
						'Failed to verify user location during login:',
						error,
					);*/
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [
		isSiwaLoggedIn,
		walletAddress,
		shouldEnforceLocationCheck,
		showNotServedModal,
	]);

	// Generate yield effect - animation only, data comes from positions
	useEffect(() => {
		if (currentState === 'generating') {
			const yieldTimer = setInterval(() => {
				setYieldProgress((prev) => {
					if (prev >= 100) return 100;
					return prev + 10;
				});
			}, 300);

			return () => {
				clearInterval(yieldTimer);
			};
		}
	}, [currentState]);

	useEffect(() => {
		async function fetchUSDCBalance() {
			if (!activeAccount || !isConnected) {
				setUsdcBalance(0);
				setFetchingBalance(false);
				return;
			}

			try {
				setFetchingBalance(true);
				// Wallet adapter provides address as string - convert to AccountAddress for getUSDCBalance
				const balance = await getUSDCBalance(
					activeAccount.address as any, // Temporary type assertion - TODO: check if getUSDCBalance signature changed
				);
				setUsdcBalance(balance);
			} catch (error) {
				// console.error('Failed to fetch USDC balance:', error);
				setUsdcBalance(0);
			} finally {
				setFetchingBalance(false);
			}
		}

		if (isConnected && activeAccount) {
			fetchUSDCBalance();
		}
	}, [isConnected, activeAccount]);

	useEffect(() => {
		async function fetchAptosBalance() {
			if (!activeAccount || !isConnected) {
				setAptosBalance(0);
				setFetchingBalance(false);
				return;
			}

			try {
				setFetchingBalance(true);
				const balance = await getAptosBalance(
					activeAccount.address as any, // Temporary type assertion - TODO: check if getUSDCBalance signature changed
				);
				setAptosBalance(balance);
			} catch (error) {
				// console.error('Failed to fetch Aptos balance:', error);
				setAptosBalance(0);
			} finally {
				setFetchingBalance(false);
			}
		}

		if (isConnected && activeAccount) {
			fetchAptosBalance();
		}
	}, [isConnected, activeAccount]);

	useEffect(() => {
		if (currentState === 'depositing' || currentState === 'generating') {
			return;
		}

		if (!isSiwaLoggedIn || !walletAddress) {
			if (currentState !== 'login') {
				/* console.log(
					'⚠️ [AtlasDashboard] User not logged in, going to login screen',
				);*/
				setCurrentState('login');
			}
			return;
		}

		if (isConnected && !contextUserId && !isUserIdLoading) {
			if (currentState !== 'loading' && currentState !== 'login') {
				setCurrentState('loading');
			}
			return;
		}

		// If still loading userId or position, ensure we're in loading state
		if (isUserIdLoading || lastPositionIdIsLoading || isLoadingPosition) {
			if (currentState !== 'loading' && currentState !== 'login') {
				setCurrentState('loading');
			}
			return;
		}

		if (!positionData?.result || !Array.isArray(positionData.result)) {
			setBalance(0);
			setTotalYield(0);

			// No position data - show empty dashboard
			if (currentState !== 'empty-dashboard') {
				/* console.log(
					'✅ Transitioning to empty-dashboard (no position data)',
				);*/
				setCurrentState('empty-dashboard');
			}
			return;
		}

		const [, , credited, finalized] = positionData.result;
		const vaultBal = parseInt(userInfo?.lastDeposit?.amountBase || '0');
		const creditedAmount = parseInt(String(credited || 0));

		let totalBalance = 0;
		let hasActivePositions = false;

		if (
			vaultBal > 0 &&
			userInfo?.lastDeposit?.posId === lastPositionId &&
			positionData.result[0] === walletAddress
		) {
			hasActivePositions = true;
			totalBalance = vaultBal;
		}

		// Update state with real data
		setBalance(convertFromBaseUnits(totalBalance));
		setTotalYield(convertFromBaseUnits(0));

		if (hasActivePositions) {
			// User has active position, show active dashboard
			if (
				currentState === 'empty-dashboard' ||
				currentState === 'loading'
			) {
				/* console.log(
					'✅ Transitioning to active-dashboard (has position)',
				);*/
				setCurrentState('active-dashboard');
			}
		} else {
			// Position withdrawn/cancelled, show deposit screen
			if (
				currentState === 'active-dashboard' ||
				currentState === 'loading'
			) {
				/* console.log(
					'✅ Transitioning to empty-dashboard (position withdrawn)',
				);*/
				setCurrentState('empty-dashboard');
			}
		}
	}, [
		positionData,
		lastPositionIdIsLoading,
		isLoadingPosition,
		isConnected,
		contextUserId,
		currentState,
		lastPositionId,
		isUserIdLoading,
		isSiwaLoggedIn,
		walletAddress,
		userInfo,
	]);

	let view: React.ReactNode;

	switch (currentState) {
		case 'login':
			view = <LoginView loading={false} />;
			break;

		case 'loading':
			view = <LoadingView />;
			break;

		case 'empty-dashboard':
			view = (
				<EmptyDashboardView
					aptosBalance={aptosBalance}
					usdcBalance={usdcBalance}
					fetchingBalance={fetchingBalance}
					depositAmount={depositAmount}
					setDepositAmount={setDepositAmount}
					hasActivePosition={hasActivePosition || false}
					onDeposit={handleDeposit}
					activeAccount={activeAccount}
					agentsList={filteredAgentsList}
					selectedAgentId={selectedAgentId}
					onSelectAgent={setSelectedAgentId}
					minAmountAllowed={minAmountAllowed}
					maxAmountAllowed={maxAmountAllowed}
				/>
			);
			break;

		case 'depositing':
			view = (
				<DepositingView balance={balance} aptosBalance={aptosBalance} />
			);
			break;

		case 'generating':
			view = (
				<GeneratingView
					balance={balance}
					totalYield={totalYield}
					yieldProgress={yieldProgress}
					aptosBalance={aptosBalance}
				/>
			);
			break;

		case 'active-dashboard':
			view = (
				<ActiveDashboardView
					aptosBalance={aptosBalance}
					balance={balance}
					totalYield={totalYieldCal}
					userInfo={userInfo}
					activeAccount={activeAccount}
					loadingPosition={
						lastPositionIdIsLoading || isLoadingPosition
					}
					vaultBalance={parseInt(
						userInfo?.lastDeposit?.amountBase || '0',
					)}
					positionData={positionData}
					userApyValue={userApyValue}
					lastPositionId={lastPositionId}
					walletAddress={walletAddress || ''}
					setUsdcBalance={setUsdcBalance}
					onLogout={handleLogout}
					agentsList={filteredAgentsList}
					selectedAgentId={selectedAgentId}
					apiBaseUrl={apiBaseUrl}
					vaultConfig={vaultConfig}
					onSelectAgent={setSelectedAgentId}
				/>
			);
			break;

		default:
			view = <LoadingView />;
	}

	return (
		<>
			{view}
			<NotServedModal
				open={showNotServedModal}
				onClose={() => setShowNotServedModal(false)}
			/>
		</>
	);
};
